import { hasStoredAuthToken } from "utils/authToken";

// Decides whether a 401 ends the session.
//
// Only flex2-api's POST /auth/user-info, the call that establishes who the
// user is, is authoritative. Any other 401, from flex2-api's own endpoints
// as much as from lcp/afms/files/..., is first checked against user-info
// (see confirmSessionDead in api/index.js). A single endpoint rejecting a
// request no longer signs the user out. That includes flex2-api endpoints
// like the yearly-inspection presence heartbeat, which in prod 401'd about
// 8 minutes after a fresh login (QC walkthrough 2026-10-08, #14).
//
// Shared by api/index.js (every api/* instance) and helpers/axiosConfig.js
// (the global axios defaults), so both follow the same rule.

const USER_INFO_PATH = /\/auth\/user-info\/?$/;

export function isUserInfoRequest(config) {
  if (!config) return false;
  const url = String(config.url || "").split("?")[0];
  if (!USER_INFO_PATH.test(url)) return false;
  // Another service's instance (its own baseURL) with a same-named route
  // doesn't count.
  const base = config.baseURL;
  return !base || base === process.env.REACT_APP_API_URL;
}

let sessionCheck = null;
// utils/session's refreshSession, registered by api/index.js — a hook for the
// same reason as the session check.
let renewSession = null;

// api/index.js registers its user-info check here; a hook rather than an
// import so this module stays free of the axios instances and the store.
export function registerSessionCheck(fn) {
  sessionCheck = fn;
}

export function registerSessionRenewal(fn) {
  renewSession = fn;
}

// Resolves true when the 401 means the session is over.
export async function shouldSignOut(error) {
  if (isUserInfoRequest(error?.config)) return true;
  // Nothing to sign out of, or nothing to confirm with.
  if (!hasStoredAuthToken() || !sessionCheck) return false;
  return sessionCheck();
}

// What to do with a 401, now that sessions can be renewed (utils/session):
//   "retry"   — the session is (now) valid with a newer token: replay it.
//   "signout" — the session is over and couldn't be renewed.
//   "keep"    — a single endpoint refused; the session stands. Fail the call.
//
// A request sent with a token that has since been renewed is replayed outright
// — at expiry every in-flight request 401s together, and only one of them
// needs to do the renewing. A dead session gets one refresh attempt before
// anyone is signed out; a replay that 401s again doesn't get a second.
export async function decide401(error) {
  const config = error?.config || {};
  const retried = Boolean(config._retriedAfterRefresh);
  if (!retried && sentWithStaleToken(config)) return "retry";
  if (!(await shouldSignOut(error))) {
    return !retried && sentWithStaleToken(config) ? "retry" : "keep";
  }
  if (!retried && renewSession && (await renewSession()) === true) return "retry";
  return "signout";
}

const authHeaderOf = (config) => {
  const h = config?.headers;
  if (!h) return null;
  return (typeof h.get === "function" ? h.get("Authorization") : null) || h.Authorization || h.authorization || null;
};

function sentWithStaleToken(config) {
  const sent = authHeaderOf(config);
  let current = null;
  try {
    current = localStorage.getItem("authToken");
  } catch (_e) {
    return false;
  }
  return Boolean(sent && current && sent !== `Bearer ${current}`);
}

// Test seam.
export function __resetSessionCheckForTests() {
  sessionCheck = null;
  renewSession = null;
}
